/**
 * Bookings Hook
 * Provides booking data and operations
 */

import { useState, useEffect, useCallback } from 'react';
import { bookingsApi, Booking, BookingSearchParams } from '@/lib/api/bookings.api';
import { ApiError } from '@/lib/api/client';

interface UseBookingsReturn {
  bookings: Booking[];
  isLoading: boolean;
  error: ApiError | null;
  getOperatorBookings: (params?: BookingSearchParams) => Promise<void>;
  getUserBookings: (params?: BookingSearchParams) => Promise<void>;
  getBookingById: (id: string) => Promise<Booking | null>;
  updateBookingStatus: (id: string, status: 'confirmed' | 'rejected', reason?: string, options?: { refresh?: boolean }) => Promise<boolean>;
  cancelBooking: (id: string, reason?: string, options?: { refresh?: boolean; scope?: 'operator' | 'user' }) => Promise<boolean>;
  markPaymentCollected: (id: string, reason?: string, options?: { refresh?: boolean }) => Promise<boolean>;
  clearError: () => void;
}

export function useBookings(): UseBookingsReturn {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  /**
   * Get operator bookings
   */
  const getOperatorBookings = useCallback(async (params?: BookingSearchParams) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await bookingsApi.getOperatorBookings(params);
      if (response.success && response.data) {
        // Handle paginated response structure: { data: Booking[], pagination: {...} }
        const bookingsArray = Array.isArray(response.data) 
          ? response.data 
          : (response.data as any)?.data || [];
        setBookings(bookingsArray);
      } else {
        setError(response.error || {
          code: 'FETCH_ERROR',
          message: 'Failed to fetch bookings',
        });
        setBookings([]);
      }
    } catch (err) {
      setError({
        code: 'FETCH_ERROR',
        message: err instanceof Error ? err.message : 'Failed to fetch bookings',
      });
      setBookings([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Get user bookings
   */
  const getUserBookings = useCallback(async (params?: BookingSearchParams) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await bookingsApi.getUserBookings(params);
      if (response.success && response.data) {
        // Handle paginated response structure: { data: Booking[], pagination: {...} }
        const bookingsArray = Array.isArray(response.data) 
          ? response.data 
          : (response.data as any)?.data || [];
        setBookings(bookingsArray);
      } else {
        setError(response.error || {
          code: 'FETCH_ERROR',
          message: 'Failed to fetch bookings',
        });
        setBookings([]);
      }
    } catch (err) {
      setError({
        code: 'FETCH_ERROR',
        message: err instanceof Error ? err.message : 'Failed to fetch bookings',
      });
      setBookings([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Get booking by ID
   */
  const getBookingById = useCallback(async (id: string): Promise<Booking | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await bookingsApi.getBookingById(id);
      if (response.success && response.data) {
        return response.data;
      } else {
        setError(response.error || {
          code: 'FETCH_ERROR',
          message: 'Failed to fetch booking',
        });
        return null;
      }
    } catch (err) {
      setError({
        code: 'FETCH_ERROR',
        message: err instanceof Error ? err.message : 'Failed to fetch booking',
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Update booking status
   */
  const updateBookingStatus = useCallback(async (
    id: string,
    status: 'confirmed' | 'rejected',
    reason?: string,
    options?: { refresh?: boolean }
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await bookingsApi.updateBookingStatus(id, status, reason);
      if (response.success) {
        if (options?.refresh !== false) {
          await getOperatorBookings();
        }
        return true;
      } else {
        setError(response.error || {
          code: 'UPDATE_STATUS_ERROR',
          message: 'Failed to update booking status',
        });
        return false;
      }
    } catch (err) {
      setError({
        code: 'UPDATE_STATUS_ERROR',
        message: err instanceof Error ? err.message : 'Failed to update booking status',
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [getOperatorBookings]);

  /**
   * Cancel booking
   */
  const cancelBooking = useCallback(async (
    id: string,
    reason?: string,
    options?: { refresh?: boolean; scope?: 'operator' | 'user' }
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await bookingsApi.cancelBooking(id, reason);
      if (response.success) {
        if (options?.refresh !== false) {
          if (options?.scope === 'operator') {
            await getOperatorBookings();
          } else {
            await getUserBookings();
          }
        }
        return true;
      } else {
        setError(response.error || {
          code: 'CANCEL_ERROR',
          message: 'Failed to cancel booking',
        });
        return false;
      }
    } catch (err) {
      setError({
        code: 'CANCEL_ERROR',
        message: err instanceof Error ? err.message : 'Failed to cancel booking',
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [getOperatorBookings, getUserBookings]);

  /**
   * Mark payment as collected
   */
  const markPaymentCollected = useCallback(async (
    id: string,
    reason?: string,
    options?: { refresh?: boolean }
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await bookingsApi.markPaymentCollected(id, reason);
      if (response.success) {
        if (options?.refresh !== false) {
          await getOperatorBookings();
        }
        return true;
      } else {
        setError(response.error || {
          code: 'MARK_PAYMENT_ERROR',
          message: 'Failed to mark payment as collected',
        });
        return false;
      }
    } catch (err) {
      setError({
        code: 'MARK_PAYMENT_ERROR',
        message: err instanceof Error ? err.message : 'Failed to mark payment as collected',
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [getOperatorBookings]);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    bookings,
    isLoading,
    error,
    getOperatorBookings,
    getUserBookings,
    getBookingById,
    updateBookingStatus,
    cancelBooking,
    markPaymentCollected,
    clearError,
  };
}
